// The live board over the API's WebSocket: a snapshot, then patches. On a dropped connection it
// reconnects with backoff and gets a new snapshot (CLAUDE.md "Projections and real-time"). A
// close with 4401 means the session ended: sign in again.

import { useEffect, useState } from 'react';
import { applyMessage, type Rows, type ServerMessage } from './board-state.ts';

export type StreamStatus = 'connecting' | 'live' | 'reconnecting';

const MAX_BACKOFF_MS = 30_000;

export function useBoardStream(siteId: string): { rows: Rows; status: StreamStatus } {
  const [rows, setRows] = useState<Rows>(new Map());
  const [status, setStatus] = useState<StreamStatus>('connecting');

  useEffect(() => {
    let socket: WebSocket | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let backoff = 1000;
    let stopped = false;

    const connect = () => {
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${scheme}://${window.location.host}/ws/sites/${siteId}/board`);
      socket.addEventListener('message', (event) => {
        const message = JSON.parse(String(event.data)) as ServerMessage;
        if (message.type === 'snapshot') {
          setStatus('live');
          backoff = 1000;
        }
        setRows((current) => applyMessage(current, message));
      });
      socket.addEventListener('close', (event) => {
        if (stopped) return;
        if (event.code === 4401) {
          window.location.assign(`/auth/login?returnTo=${encodeURIComponent('/')}`);
          return;
        }
        setStatus('reconnecting');
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      });
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(retry);
      socket?.close();
    };
  }, [siteId]);

  return { rows, status };
}
